import { Link } from "wouter";
import FaqAccordion from "@/components/FaqAccordion";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import { AnimatedStatGrid } from "@/components/AnimatedStat";
import { ArrowRight, Compass, PenTool, Users, Megaphone, LineChart, Lightbulb, CheckCircle2, ChevronDown } from "lucide-react";
import Header from "@/components/Header";
import SEOHead from "@/components/SEOHead";
import SharePostRow from "@/components/SharePostRow";


import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";
import { AnswerFirstSections } from "@/components/AnswerFirstSections";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import SchemaScript from "@/components/SchemaScript";
const pageTitle = "AI Marketing Strategy UK | Persona-Led GTM";
const pageDescription = "AI marketing strategy for B2B: one documented buyer persona across research, paid execution and sales evidence. A plan, not a guaranteed outcome.";
const answerBlock = "An AI marketing strategy sets out who you sell to, which questions they ask and which channels should answer them, then ties paid media, AI search and agentic workflows to one documented buyer persona. Integrated.Social tests the plan in market rather than filing it. A strategy is a plan, not a guaranteed outcome. It suits B2B teams whose channels disagree about the buyer; start with a Free AI Visibility Score or book a 30-minute call.";
const faqSummary = "These answers cover what an AI marketing strategy is, what it includes, who it suits, how it differs from buying a tool and how progress is measured. In short, the plan starts from one documented buyer persona, sets which channel answers which question, keeps a named senior person accountable for every claim and judges progress by decisions improved and pipeline evidence, not by the number of outputs produced.";
const faqs = [
  {
    q: "What is an AI marketing strategy?",
    a: "An AI marketing strategy sets out who you sell to, which questions they ask and which channels should answer them, then ties paid media, AI search and agentic workflows to one documented buyer persona. Integrated.Social tests the plan in market rather than filing it. A strategy is a plan, not a guaranteed outcome.",
  },
  {
    q: "What is an AI marketing persona?",
    a: "An AI marketing persona is a documented buying profile used consistently across paid media, answer-engine pages, sales enablement and agent-assisted drafts. It identifies the buyer’s job, evidence needs, objections, decision context and the human owner who approves claims before publication.",
  },
  {
    q: "How does persona connect to AEO and paid?",
    a: "The persona sets the buyer question and approved commercial claim. AEO makes the evidence easy for answer engines to retrieve; paid media tests the same proposition with the same buyer. The work is joined by one measurement plan, not by separate channel slogans.",
  },
  {
    q: "Who reviews an AI marketing persona?",
    a: "A named commercial owner reviews the persona, evidence boundaries and any claim that will be published or used in paid media. Agents may draft, organize research and propose variants; they do not replace accountable review.",
  },
  {
    q: "How do you measure an AI marketing strategy?",
    a: "We count decisions improved, not outputs generated. Each month we log the buyer questions tested by engine, prompt, date, market and cited URL, alongside qualified inquiries and pipeline evidence from your CRM. One screenshot is an anecdote, so we only act on repeated observations, and every material change is signed off by a named senior person.",
  },
];

export default function ServiceStrategy() {

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/ai-marketing-strategy/#webpage",
        url: "https://integrated.social/services/ai-marketing-strategy/",
        name: pageTitle,
        headline: pageTitle,
        abstract: answerBlock,
        description: pageDescription,
        isPartOf: { "@id": "https://integrated.social/#website" },
        about: { "@id": "https://integrated.social/#organization" },
        publisher: { "@id": "https://integrated.social/#organization" },
        breadcrumb: { "@id": "https://integrated.social/services/ai-marketing-strategy/#breadcrumb" },
        inLanguage: "en-US",
        dateModified: "2026-11-02"
      },
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/ai-marketing-strategy/#service",
        name: pageTitle,
        url: "https://integrated.social/services/ai-marketing-strategy/",
        provider: { "@id": "https://integrated.social/#organization" },
        serviceType: "AI Marketing Strategy",
        description: "AI marketing strategy for B2B companies and B2C brands: one documented buyer persona across research, paid execution, sales evidence and controlled agentic workflows. A plan, not a guaranteed outcome.",
        audience: [
          { "@type": "BusinessAudience", audienceType: "B2B companies" },
          { "@type": "Audience", audienceType: "B2C and ecommerce brands" }
        ],
        subjectOf: [
          { "@type": "Article", url: "https://integrated.social/blog/ai-agents-replacing-b2b-research-phase/" },
          { "@type": "Article", url: "https://integrated.social/blog/ai-capex-2026-b2b-marketing-budget-implications/" },
          { "@type": "Article", url: "https://integrated.social/blog/openai-cannes-2026-chatgpt-ads-b2b-commercial-intent/" }
        ],
        areaServed: [
          { "@type": "Country", name: "United Kingdom" },
          { "@type": "Country", name: "United States" },
          { "@type": "Place", name: "Global" }
        ],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Strategy and Content Services",
          itemListElement: [
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Persona-Driven GTM Strategy" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "GenAI Content Operations" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Brand Positioning and Messaging" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Marketing Automation and Workflows" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Full-Funnel Attribution and Reporting" } }
          ]
        }
      },
      {
        "@type": "FAQPage",
        "@id": "https://integrated.social/services/ai-marketing-strategy/#faq",
        isPartOf: { "@id": "https://integrated.social/services/ai-marketing-strategy/#webpage" },
        mainEntity: faqs.map(f => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://integrated.social/services/ai-marketing-strategy/#breadcrumb",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://integrated.social/" },
          { "@type": "ListItem", position: 2, name: "Services", item: "https://integrated.social/services/" },
          { "@type": "ListItem", position: 3, name: "AI Marketing Strategy", item: "https://integrated.social/services/ai-marketing-strategy/" }
        ]
      }
    ]
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        ogImage="https://integrated.social/media/iso-gtm-strategy_1b5eacd1.png"
        description={pageDescription}
        canonical="https://integrated.social/services/ai-marketing-strategy/"
      />
      <SchemaScript schema={JSON.stringify(schemaData)} />
      <div className="min-h-screen bg-background">
        <Header />
        <main>
        {/* Breadcrumb */}
        <nav className="container pt-24 pb-4" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-muted-foreground">
            <li><Link href="/" className="hover:text-foreground transition-colors">Home</Link></li>
            <li>/</li>
            <li><Link href="/services/ai-marketing-strategy/" className="hover:text-foreground transition-colors">Services</Link></li>
            <li>/</li>
            <li className="text-foreground font-medium">AI Marketing Strategy</li>
          </ol>
        </nav>

        {/* Hero */}
        <header className="container pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="max-w-4xl">
            <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 rounded-full mb-6">
              Is your marketing strategy generating pipeline, or just activity?
            </span>
            <h1 className="font-serif headline-xl mb-6">
              AI Marketing Strategy UK: Persona-Led GTM
            </h1>
            <p data-answer-block className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed mb-6">
              {answerBlock}
            </p>
            {/* RTB strip */}
            <div className="grid grid-cols-3 gap-3 mb-6 max-w-xl">
              <div className="border border-[#FF8C00]/30 bg-[#FF8C00]/5 rounded-sm px-3 py-2 text-center">
                <div className="text-[#FF8C00] font-bold text-base">20+ years</div>
                <div className="text-muted-foreground text-xs mt-0.5">Senior experience across paid and earned marketing</div>
              </div>
              <div className="border border-[#00D4AA]/30 bg-[#00D4AA]/5 rounded-sm px-3 py-2 text-center">
                <div className="text-[#00D4AA] font-bold text-base">+312%</div>
                <div className="text-muted-foreground text-xs mt-0.5">Qualified pipeline in 90 days for B2B SaaS</div>
              </div>
              <div className="border border-border rounded-sm px-3 py-2 text-center">
                <div className="text-foreground font-bold text-base">Full-funnel</div>
                <div className="text-muted-foreground text-xs mt-0.5">Strategy to execution, measured against agreed evidence</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London" href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_strategy"); trackSectionCtaClick({ section_name: "service_strategy_hero", cta_text: "Book your 30-minute strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}>
                Book a 30-minute strategy call <ArrowRight className="w-4 h-4" />
              </a>
              <Link href="/blog/ai-capex-2026-b2b-marketing-budget-implications/"
                className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-medium rounded-lg hover:bg-accent transition-all duration-200">
                Read: AI Capex and your 2026 budget
              </Link>
            </div>
          </div>
          <div className="hidden lg:block">
            <img
              src="/media/iso-gtm-strategy-1200_d11388aa.webp"
              srcSet="/media/iso-gtm-strategy-800_4a7deae8.webp 800w, /media/iso-gtm-strategy-1200_d11388aa.webp 1200w"
              sizes="(max-width: 1024px) 100vw, 50vw"
              alt="AI growth marketing strategy and GTM deployment for B2B: persona intelligence, ICP definition, agentic AI systems, and pipeline generation for enterprise brands in London"
              className="w-full h-auto rounded-xl"
              width={1200}
              height={800}
              loading="lazy"
              decoding="async"
            />
          </div>
          </div>
        </header>
        <AnswerFirstSections
          methodTitle="How we build the strategy"
          method={<p>We use the (Re)Target framework, a circular eight-stage framework that incorporates real-time analytics feedback, psychological trigger mapping and continuous audience discovery. The persona it produces is used across research, message testing, paid execution and sales evidence, so the plan is tested in market rather than filed.</p>}
          fit={<><p><strong>For:</strong> B2B leadership teams that need a strategy someone will actually deploy.</p><p className="mt-2"><strong>Not for:</strong> teams looking only for a slide deck or a tool shortlist.</p></>}
          evidence={<><p><strong>Published example:</strong> The UK FinTech case study documents a persona pivot and structured A/B testing for a VC-backed personal finance app. <Link href="/case-studies/fintech-persona-targeting/" className="underline underline-offset-4">Read the fintech case study</Link>.</p><p className="mt-3"><strong>Verified result:</strong> The B2B SaaS ABM case study reports +312% qualified pipeline in 90 days. <Link href="/case-studies/b2b-saas-abm/" className="underline underline-offset-4">Read the ABM case study</Link>. ABM only.</p><p className="mt-3"><strong>Future objective:</strong> A persona and channel plan your team can deploy and measure.</p></>}
        />

        <section className="border-y border-border bg-accent/30 py-16" aria-labelledby="persona-service-heading">
          <div className="container grid gap-8 lg:grid-cols-[1.1fr_.9fr]">
            <div>
              <span className="font-mono text-xs uppercase tracking-widest text-[#FF8C00]">Persona-led GTM</span>
              <h2 id="persona-service-heading" className="mt-3 font-serif text-3xl md:text-4xl">AI marketing personas sales can work from</h2>
              <p className="mt-5 max-w-3xl text-muted-foreground leading-relaxed">A usable persona is not a decorative segment or a prompt template. It is one buyer definition that sales, paid media and answer-ready content can all recognize. It records the job to be done, the proof needed to progress, the objection that must be answered and who may approve the claim.</p>
              <p className="mt-4 text-muted-foreground leading-relaxed">Read the <Link href="/blog/ai-marketing-persona-paid-and-earned/" className="text-[#C65F00] hover:underline">paid-and-earned persona guide</Link>, see the proof-only <Link href="/case-studies/fintech-persona-targeting/" className="text-[#C65F00] hover:underline">FinTech case study</Link>, or connect the plan to <Link href="/services/account-based-marketing/" className="text-[#C65F00] hover:underline">ABM</Link> and the <Link href="/" className="text-[#C65F00] hover:underline">UK AI marketing agency hub</Link>.</p>
            </div>
            <aside className="rounded-xl border border-border bg-background p-6">
              <h3 className="font-serif text-2xl">What “AI marketing persona” means here</h3>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">It is the shared decision reference for paid and earned activity. Agents may prepare research and variants, but a named commercial owner reviews the persona, evidence threshold and claim before an ad, page or answer-facing asset is published.</p>
            </aside>
          </div>
        </section>

        {/* What We Deliver */}
        <section className="bg-[#1a1a1a] text-white py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-4">What we deliver</h2>
            <p className="text-gray-400 text-lg max-w-2xl mb-12">
              Strategy is only valuable when it is deployed. Here is what you get, running, not planned.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { icon: Users, title: "Persona-Driven GTM", desc: "Deep buyer persona development using data + qualitative research. ICP definition, buying committee mapping, and message-market fit testing across channels." },
                { icon: Compass, title: "Positioning & Messaging", desc: "Competitive positioning that differentiates. Brand narrative, value propositions, and messaging frameworks that work across ads, content, sales enablement, and landing pages." },
                { icon: PenTool, title: "GenAI Content Ops", desc: "Managed content workflow: research → draft → edit → approval → publish, with brand governance, AEO readiness, and editorial quality control." },
                { icon: Megaphone, title: "Channel Strategy", desc: "Where to play and how to win. Organic vs paid allocation, LinkedIn vs search vs email, and the sequencing that builds compound returns over time." },
                { icon: Lightbulb, title: "Marketing Automation", desc: "Documented workflows: lead nurturing sequences, scoring models, CRM enrichment, and trigger-based campaigns, with clear ownership and release decisions." },
                { icon: LineChart, title: "Attribution & Reporting", desc: "Full-funnel measurement: awareness → engagement → qualified inquiry → pipeline evidence. Every channel and campaign is reviewed against the same decision record." }
              ].map((item, i) => (
                <div key={i} className="p-6 border border-white/10 rounded-lg hover:border-[#FF8C00]/40 transition-colors">
                  <item.icon className="w-8 h-8 text-[#FF8C00] mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Audio Narrative - voice summary of this service page */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text="AI Marketing Strategy for B2B Brands. Persona-led strategy connects research, paid execution, AI search, sales evidence and controlled agentic workflows. It starts with the buyer questions, records the proof needed for a decision and keeps a named senior person accountable for material claims."
            title="AI Marketing Strategy for B2B Brands"
            readingTime={5}
          />
        </div>

        {/* Art & Science */}
        <section className="py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-12">The art & science of marketing strategy</h2>
            <div className="grid md:grid-cols-2 gap-16">
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-[#FF8C00]">The Art</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Marketing is fundamentally about understanding people, their fears, aspirations, decision-making patterns, and the language they use to describe their problems. No amount of AI can replace the human insight needed to craft positioning that resonates, messaging that converts, and brand narratives that build lasting preference.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  After 20+ years in digital marketing, with a design background, an MBA and £20m+ in paid media managed, I bring pattern recognition that no prompt can replicate. I have seen what works across market conditions, budget levels, and competitive landscapes.
                </p>
              </div>
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-[#FF8C00]">The Science</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Once you know what to say and who to say it to, execution becomes a systems problem. This is where agentic AI transforms marketing: autonomous content production, evidence-led optimization, systematic A/B testing, and data-driven decision-making at a speed and scale impossible for human teams alone.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  We deploy multi-agent systems that handle repetitive marketing work, such as research, first drafts, data enrichment and reporting, freeing senior people to focus on strategy, relationships and creative work.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-[#0d0d0d] text-white"><div className="container"><h2 className="font-serif headline-lg mb-4">What the strategy records</h2><p className="text-gray-400 text-lg max-w-2xl">A documented buyer, the evidence needed to progress a decision, a channel plan and the measures used to decide the next change. Strategy is a testable plan, not a forecast.</p></div></section>
        {/* Pillar-Spoke Links */}
        <section className="bg-accent/50 py-16">
          <div className="container">
            <h2 className="font-serif text-2xl md:text-3xl mb-8">Related services & reading</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Link href="/services/gemini-agentic-ai/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">Gemini Enterprise Agentic AI</h3>
                <p className="text-muted-foreground text-sm mt-2">The technology behind our deployed content and GTM systems.</p>
              </Link>
              <Link href="/services/seo-aeo-geo/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">SEO, AEO & GEO</h3>
                <p className="text-muted-foreground text-sm mt-2">AI search visibility that feeds your content strategy.</p>
              </Link>
              <Link href="/services/ppc-performance-max/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">PPC & Performance Max</h3>
                <p className="text-muted-foreground text-sm mt-2">Paid amplification of your organic content strategy.</p>
              </Link>
              <Link href="/services/seo-aeo-geo/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">AI Content Operations</h3>
                <p className="text-muted-foreground text-sm mt-2">The execution layer that deploys your content strategy at scale - answer-first modules, schema-optimized, citation-tracked.</p>
              </Link>
              <Link href="/free-ai-growth-audit/" className="block p-5 border border-border rounded-lg hover:border-[#FF8C00]/40 transition-colors bg-background">
                <span className="text-xs uppercase tracking-wider text-[#FF8C00]">Service</span>
                <h3 className="text-lg font-semibold mt-2">AI Visibility Audit</h3>
                <p className="text-muted-foreground text-sm mt-2">The starting point: a full citation baseline before strategy is built.</p>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-20 overflow-hidden"><div className="container"><div className="grid lg:grid-cols-2 gap-12 items-center"><div><span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 rounded-full mb-6">Published case study</span><h2 className="font-serif text-3xl md:text-4xl leading-tight mb-6">Persona work can make the next test more useful.</h2><p className="text-muted-foreground text-lg leading-relaxed mb-6">The UK FinTech case study documents a persona pivot and structured A/B testing for a VC-backed personal finance app. It is a published example, not a promise for another program.</p><Link href="/case-studies/fintech-persona-targeting/" className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF8C00] text-white font-semibold rounded-lg hover:bg-[#E07B00]">Read the fintech case study <ArrowRight className="w-4 h-4" /></Link></div></div></div></section>
        <section className="py-16 bg-[#0D1220] text-white"><div className="container"><h2 className="font-serif text-3xl">Integrated marketing communications</h2><p className="mt-4 max-w-3xl text-gray-400">See the published destination-marketing case study in the integrated marketing communications context.</p><Link href="/case-studies/destination-marketing/" className="mt-5 inline-flex items-center gap-2 text-amber-400 hover:underline">See our IMC campaign case study <ArrowRight className="w-4 h-4" /></Link></div></section>
        {/* Commercial KPIs */}
        <section className="py-16 bg-[#0F1419]">
          <div className="container">
            <span className="text-mono text-[#FF8C00] block mb-3 text-xs uppercase tracking-widest">Commercial KPIs</span>
            <h2 className="font-serif text-2xl md:text-3xl text-white mb-2">How we assess an AI marketing strategy</h2>
            <p className="text-white/60 text-base max-w-2xl mb-8">Strategy without measurement is just a slide deck. We review decisions, qualified inquiries and pipeline evidence against the buyer questions the plan was designed to answer.</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              {[
                { metric: "Decision evidence", description: "The buyer questions tested, approved claims used and evidence captured for the next decision" },
                { metric: "Qualified inquiry context", description: "Qualified inquiries reviewed by persona, source and commercial context" },
              ].map((kpi, i) => (
                <div key={i} className="flex items-start gap-4 p-5 border border-white/10 rounded-sm hover:border-[#FF8C00]/30 transition-colors">
                  <span className="text-[#FF8C00] text-2xl font-serif leading-none mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="text-white font-semibold text-base mb-1">{kpi.metric}</h3>
                    <p className="text-white/50 text-sm">{kpi.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <a href="/#kpis" className="text-[#FF8C00] text-sm font-medium hover:underline">← See all 6 AI marketing KPIs on the homepage</a>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20">
          <div className="container max-w-3xl">
            <h2 className="font-serif text-3xl md:text-4xl mb-3">AI marketing strategy questions, answered</h2>
            <p data-faq-summary className="mb-8 text-muted-foreground leading-relaxed">{faqSummary}</p>
            <div className="space-y-4">
              <FaqAccordion
                  items={faqs}
                  className="space-y-4"
                  itemClassName="border border-border rounded-lg overflow-hidden"
                  buttonClassName="w-full flex items-center justify-between p-5 text-left hover:bg-accent/50 transition-colors"
                  questionClassName="m-0"
                  answerClassName="px-5 pb-5 text-muted-foreground leading-relaxed"
                  iconClassName="w-5 h-5 shrink-0 transition-transform duration-200"
                />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#FF8C00] py-16">
          <div className="container text-center">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">Ready to discuss a GTM system?</h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Book a 30-minute call to discuss your buyer questions, current evidence and the next practical decision for your marketing program.
            </p>
            <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London" href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1a1a1a] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-200 active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_strategy"); trackSectionCtaClick({ section_name: "service_strategy_hero", cta_text: "Book your 30-minute strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}>
              Book your 30-minute strategy call <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>

        {/* Final CTA - three ways to work with us */}
        <section id="get-started" className="py-20 lg:py-28 bg-[#0F1419]" aria-labelledby="service-final-cta-heading">
          <div className="container">
            <div className="text-center mb-14">
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Ready to start?</span>
              <h2 id="service-final-cta-heading" className="font-serif text-3xl md:text-4xl text-white mb-4">
                Three ways to work with us
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                Pick the path that fits where you are right now.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">Free · No obligation</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Audit your domain for AI Answers</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Use the Free AI Visibility Score to review your site’s readiness across seven assessment areas, then decide whether a paid diagnostic is appropriate.
                </p>
                <a aria-label="Check my AI visibility"
                  href="/free-ai-growth-audit/"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF8C00] text-[#0F1419] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#E07B00] transition-colors active:scale-[0.97]" onClick={() => trackSectionCtaClick({ section_name: "service_strategy_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                >
                  Check my AI visibility<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">1-to-1 · Personalized</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">AI Executive Training for Leaders</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Three sessions built around your role, your sector, and your goals. Hands-on AI training that makes you dangerous with the tools your competitors are still figuring out.
                </p>
                <a
                  href="/services/ai-training-for-leaders/"
                  className="inline-flex items-center justify-center gap-2 border border-[#FF8C00] text-[#FF8C00] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#FF8C00] hover:text-[#0F1419] transition-colors active:scale-[0.97]"
                >
                  Explore AI Training
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">30 min · No pitch</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Discuss your AI marketing strategy</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  A focused conversation about where you are, where you want to be, and what a realistic 90-day AI marketing deployment looks like for your business. No deck. No sales script.
                </p>
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                  href="https://calendly.com/modielnadi/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 px-6 py-3.5 text-sm font-bold rounded-sm hover:border-[#FF8C00] hover:text-[#FF8C00] transition-colors active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_strategy"); trackSectionCtaClick({ section_name: "service_strategy_hero", cta_text: "Book your 30-minute strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
                >
                  Schedule a discovery call
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
            </div>
          </div>
        </section>
          <section className="mx-auto max-w-5xl px-4 pb-12" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_end" /></section>
        </main>
      </div>
      {/* Arabic-aware share row */}
      <div className="container max-w-5xl mx-auto px-4 pb-16">
        <SharePostRow
          title={pageTitle}
          canonicalUrl="https://integrated.social/services/ai-marketing-strategy/"
          excerpt="A persona-led AI marketing strategy that connects research, paid execution, AI search and sales evidence without promising an outcome."
        />
      </div>

      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
